/**
 * HTTP client for the Cloudflare Worker API.
 *
 * Reads `VITE_API_BASE` (defaults to `http://localhost:8787`) and
 * surfaces every Phase-7 GET endpoint as a typed function. Errors are
 * normalized to a thrown `ApiError` so callers can branch on `status`.
 *
 * Worker responses
 * ────────────────
 * - `/api/daily`               → `DailyListResponse` (n/a: the legacy
 *                                `?days=N` route lives in `worker/src/index.ts`,
 *                                not in `router.ts`, and the frontend's
 *                                Round A `daily` view does not hit it.)
 * - `/api/daily/:date`         → `DailyByDateResponse`
 * - `/api/weeks`               → `WeeksListResponse`
 * - `/api/week/:id`            → `Week`
 * - `/api/article/:pmid`       → `ArticleResponse`
 * - `/api/changelog`           → `ChangelogResponse`
 *
 * The `fetchDaily(days)` helper here aggregates `dailyByDate` for the
 * most recent `days` epdats; the store keeps a tiny LRU so the daily
 * view doesn't double-fetch.
 */

import type {
  ArticleDetail,
  ArticleResponse,
  ChangelogResponse,
  ChangelogRow,
  DailyByDateResponse,
  DailyDayGroup,
  DailyListItem,
  Week,
  WeekSummary,
  WeeksListResponse,
} from "./types";

const baseUrl = (import.meta.env.VITE_API_BASE ?? "http://localhost:8787").replace(
  /\/+$/u,
  "",
);

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = "ApiError";
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string };
  message?: string;
}

async function getJson<T>(path: string): Promise<T> {
  const url = `${baseUrl}${path}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: { accept: "application/json" },
    });
  } catch (err) {
    throw new ApiError(
      0,
      `network error: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  if (!res.ok) {
    let code: string | undefined;
    let message: string;
    try {
      const body = (await res.json()) as ErrorBody;
      code = body.error?.code;
      message = body.error?.message ?? body.message ?? `HTTP ${res.status}`;
    } catch {
      message = `HTTP ${res.status}`;
    }
    throw new ApiError(res.status, message, code);
  }
  try {
    return (await res.json()) as T;
  } catch (err) {
    throw new ApiError(
      res.status,
      `invalid JSON: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

/**
 * Per-day fetch (worker `GET /api/daily/:date`). Returns the bilingual
 * daily item list for a single Beijing calendar day. Empty if nothing
 * has been ingested for that day.
 */
export async function fetchDailyByDate(date: string): Promise<DailyListItem[]> {
  const body = await getJson<DailyByDateResponse>(
    `/api/daily/${encodeURIComponent(date)}`,
  );
  return body.items ?? [];
}

/**
 * Aggregate fetch for the daily view. Iterates `days` calendar days back
 * from today (Beijing) and groups whatever was ingested. Days with no
 * articles are simply omitted from the result.
 */
export async function fetchDaily(days: number = 7): Promise<DailyDayGroup[]> {
  const count = Math.max(1, Math.min(days, 60));
  const dates = recentDates(count);
  const groups = await Promise.all(
    dates.map(async (epdat) => {
      try {
        const items = await fetchDailyByDate(epdat);
        return { epdat, items };
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return { epdat, items: [] as DailyListItem[] };
        }
        throw err;
      }
    }),
  );
  return groups.filter((g) => g.items.length > 0);
}

export function fetchWeek(id: string): Promise<Week> {
  return getJson<Week>(`/api/week/${encodeURIComponent(id)}`);
}

export async function fetchWeeks(): Promise<WeekSummary[]> {
  const body = await getJson<WeeksListResponse>(`/api/weeks`);
  return body.weeks ?? [];
}

export async function fetchArticle(pmid: string): Promise<ArticleDetail> {
  const body = await getJson<ArticleResponse>(
    `/api/article/${encodeURIComponent(pmid)}`,
  );
  if (!body.article) {
    throw new ApiError(404, "article not found", "article_not_found");
  }
  return body.article;
}

export async function fetchChangelog(limit: number = 50): Promise<ChangelogRow[]> {
  const capped = Math.max(1, Math.min(limit, 200));
  const body = await getJson<ChangelogResponse>(
    `/api/changelog?limit=${capped}`,
  );
  return body.entries ?? [];
}

function recentDates(n: number): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 0; i < n; i += 1) {
    const d = new Date(now.getTime());
    d.setUTCDate(d.getUTCDate() - i);
    out.push(toIsoDate(d));
  }
  return out;
}

function toIsoDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export const API_BASE = baseUrl;
